import { Check, FileText, Film, Image as ImageIcon, Upload, X } from 'lucide-react';
import { useState } from 'react';
import { formatBytes } from '../../lib/formatBytes';
import { HelpTip } from '../HelpTip';

/** Where one picked file stands while the form files it. */
export type FileUploadState = { percent: number } | { done: true } | { error: string };

/**
 * A box for SEVERAL files, each filed on its own once the record they belong to exists.
 *
 * **A DROP ZONE**: drag files onto it, or click it to browse. It STATES what it takes —
 * `hint`, the allowed types and the limit — on its own face, because that is the question the
 * reader has before choosing a file, and a help icon is where they would never look for it.
 * The longer `description` stays in the `<HelpTip>`, with its `sr-only` twin, like every input.
 *
 * **Each picked file is a ROW** — a thumbnail for a picture (the reader's own file, in their own
 * tab), a type glyph otherwise, the name, the size, and a remove — because a multiple file input
 * cannot drop one of its own. While the form files them, `uploads` puts each row's own progress,
 * tick or refusal ON that row, so the reader sees which file is going and which one failed.
 *
 * **The `accept` filter and the size check are conveniences, not gates** — the server verifies the
 * bytes and enforces its own cap. An oversize file is named on its row so the reader is not sent
 * to the server to find out; a dropped file the picker would have filtered is still listed, and
 * the server refuses it by its bytes.
 *
 * The input itself is visually hidden but FOCUSABLE — Tab reaches it, and the zone draws the
 * focus ring — so the keyboard path is the browser's own file picker, unchanged.
 */
export function FilesField({
  id,
  label,
  description,
  hint,
  accept,
  maxBytes,
  files,
  onChange,
  uploads,
  disabled = false,
  error,
}: {
  id: string;
  label: string;
  description: string;
  /** What the zone takes, in the reader's words — `PDF, PNG, JPEG or MP4`. Omitted, the zone states the limit alone. */
  hint?: string;
  /** The `accept` attribute — a convenience for the picker, never a check. */
  accept: string;
  /** The per-file limit the server enforces. */
  maxBytes: number;
  files: File[];
  onChange: (files: File[]) => void;
  /** Each row's state, by index — progress while filing, a refusal after. The caller clears it when the list changes. */
  uploads?: Record<number, FileUploadState>;
  disabled?: boolean;
  error?: string;
}) {
  const [dragging, setDragging] = useState(false);
  const oversize = files.filter((file) => file.size > maxBytes);
  const locked = disabled;

  function add(picked: File[]) {
    if (picked.length > 0) onChange([...files, ...picked]);
  }

  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex min-h-5 items-center gap-1.5">
        <span id={`${id}-label`} className="text-sm text-slate-700">
          {label}
        </span>

        <HelpTip label={description} />
      </div>

      <span id={`${id}-desc`} className="sr-only">
        {description}
      </span>

      <label
        htmlFor={id}
        onDragOver={(event) => {
          // Only a drag carrying FILES lights the zone — a dragged text selection is not a file.
          if (locked || !event.dataTransfer.types.includes('Files')) return;

          event.preventDefault();
          setDragging(true);
        }}
        onDragLeave={(event) => {
          // Fires on every child boundary too; only leaving the zone itself counts.
          if (event.currentTarget.contains(event.relatedTarget as Node | null)) return;

          setDragging(false);
        }}
        onDrop={(event) => {
          if (locked) return;

          event.preventDefault();
          setDragging(false);
          add(Array.from(event.dataTransfer.files ?? []));
        }}
        className={`flex flex-col items-center justify-center gap-1 rounded-lg border border-dashed px-4 py-5 text-center transition-colors focus-within:ring-2 focus-within:ring-blue-200 ${
          locked
            ? 'cursor-not-allowed border-slate-200 bg-slate-50'
            : dragging
              ? 'cursor-copy border-blue-400 bg-blue-50'
              : oversize.length > 0 || error
                ? 'cursor-pointer border-red-300 bg-white hover:bg-slate-50'
                : 'cursor-pointer border-slate-300 bg-white hover:border-slate-400 hover:bg-slate-50'
        }`}
      >
        <Upload className={`h-5 w-5 ${dragging ? 'text-blue-500' : 'text-slate-400'}`} aria-hidden="true" />
        <span className="text-sm text-slate-600">
          {dragging ? (
            'Drop to add'
          ) : (
            <>
              Drag files here, or <span className="font-medium text-blue-600">browse</span>
            </>
          )}
        </span>
        <span className="text-xs text-slate-500">
          {hint ? `${hint}, up` : 'Up'} to <span className="font-mono tabular-nums">{formatBytes(maxBytes)}</span> each
        </span>

        <input
          id={id}
          name={id}
          type="file"
          multiple
          accept={accept}
          disabled={locked}
          aria-labelledby={`${id}-label`}
          aria-describedby={`${id}-desc`}
          aria-invalid={oversize.length > 0 || Boolean(error)}
          // Appended, so picking twice adds rather than replaces; the value is cleared so the same
          // file can be picked again after it was removed.
          onChange={(event) => {
            const picked = Array.from(event.target.files ?? []);
            event.target.value = '';
            add(picked);
          }}
          className="sr-only"
        />
      </label>

      {files.length > 0 ? (
        <ul aria-label={`${label} picked`} className="flex flex-col divide-y divide-slate-200 rounded-lg border border-slate-200">
          {files.map((file, index) => (
            <FileRow
              key={`${file.name}-${file.size}-${index}`}
              id={`${id}-remove-${index}`}
              file={file}
              tooLarge={file.size > maxBytes}
              maxBytes={maxBytes}
              upload={uploads?.[index]}
              onRemove={locked ? null : () => onChange(files.filter((_, i) => i !== index))}
            />
          ))}
        </ul>
      ) : null}

      {oversize.length > 0 ? (
        <p className="text-xs text-red-700">
          {oversize.length === 1 ? `${oversize[0].name} is` : `${oversize.length} files are`} over the{' '}
          {formatBytes(maxBytes)} limit. Remove {oversize.length === 1 ? 'it' : 'them'} to continue.
        </p>
      ) : error ? (
        <p className="text-xs text-red-700">{error}</p>
      ) : null}
    </div>
  );
}

function FileRow({
  id,
  file,
  tooLarge,
  maxBytes,
  upload,
  onRemove,
}: {
  id: string;
  file: File;
  tooLarge: boolean;
  maxBytes: number;
  upload: FileUploadState | undefined;
  onRemove: (() => void) | null;
}) {
  const picture = file.type === 'image/png' || file.type === 'image/jpeg';
  const failed = upload && 'error' in upload ? upload.error : null;
  const percent = upload && 'percent' in upload ? upload.percent : null;
  const done = Boolean(upload && 'done' in upload);

  return (
    <li className="flex flex-col gap-1.5 px-3 py-2">
      <div className="flex items-center gap-3">
        <span className="flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded border border-slate-200 bg-slate-50">
          {picture ? (
            <img
              alt=""
              className="h-full w-full object-cover"
              /*
                The reader's OWN file, drawn in their own tab. The URL is made when the element
                mounts and revoked when it goes (a React 19 ref cleanup), so no state and no
                effect hold it.
              */
              ref={(img) => {
                if (!img) return;
                const url = URL.createObjectURL(file);
                img.src = url;
                return () => URL.revokeObjectURL(url);
              }}
            />
          ) : file.type === 'video/mp4' ? (
            <Film className="h-5 w-5 text-slate-400" aria-hidden="true" />
          ) : file.type.startsWith('image/') ? (
            <ImageIcon className="h-5 w-5 text-slate-400" aria-hidden="true" />
          ) : (
            <FileText className="h-5 w-5 text-slate-400" aria-hidden="true" />
          )}
        </span>

        <span className="flex min-w-0 flex-1 flex-col">
          <span className="truncate text-sm text-slate-600" title={file.name}>
            {file.name}
          </span>
          <span className={`font-mono text-xs tabular-nums ${tooLarge ? 'text-red-700' : 'text-slate-500'}`}>
            {formatBytes(file.size)}
            {tooLarge ? ` — over the ${formatBytes(maxBytes)} limit` : ''}
          </span>
        </span>

        {done ? (
          <span className="flex shrink-0 items-center gap-1 text-xs text-green-700">
            <Check className="h-4 w-4" aria-hidden="true" />
            Filed
          </span>
        ) : percent !== null ? (
          <span className="shrink-0 font-mono text-xs tabular-nums text-slate-500">{percent}%</span>
        ) : onRemove ? (
          <button
            type="button"
            id={id}
            onClick={onRemove}
            aria-label={`Remove ${file.name}`}
            className="shrink-0 rounded p-1 text-slate-500 hover:bg-slate-100 hover:text-slate-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-200"
          >
            <X className="h-4 w-4" aria-hidden="true" />
          </button>
        ) : null}
      </div>

      {percent !== null ? (
        <div
          role="progressbar"
          aria-label={`Filing ${file.name}`}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={percent}
          className="h-1 overflow-hidden rounded-full bg-slate-100"
        >
          <div className="h-full rounded-full bg-blue-500 transition-[width]" style={{ width: `${percent}%` }} />
        </div>
      ) : null}

      {failed ? <p className="text-xs text-red-700">{failed}</p> : null}
    </li>
  );
}
